import { useEffect, type RefObject } from "react";

const LAYER_ID = "millennials-data-tooltip-layer";

function getOrCreateLayer(): HTMLDivElement {
  let layer = document.getElementById(LAYER_ID) as HTMLDivElement | null;
  if (!layer) {
    layer = document.createElement("div");
    layer.id = LAYER_ID;
    layer.className = "millennials-data-tooltip-layer";
    layer.setAttribute("role", "tooltip");
    document.body.appendChild(layer);
  }
  return layer;
}

/**
 * Un solo nodo de tooltip en `document.body` y delegación de eventos en el contenedor.
 * Los triggers usan el atributo `data-tooltip`.
 */
export function useDataTooltipDelegation(
  containerRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const layer = getOrCreateLayer();
    let activeTarget: HTMLElement | null = null;

    const positionLayer = (target: HTMLElement) => {
      const text = target.getAttribute("data-tooltip");
      if (!text) {
        return;
      }
      layer.textContent = text;
      layer.classList.add("is-visible");
      const rect = target.getBoundingClientRect();
      layer.style.left = `${rect.left + rect.width / 2}px`;
      layer.style.top = `${rect.top}px`;
    };

    const hideLayer = () => {
      layer.classList.remove("is-visible");
      activeTarget = null;
    };

    const onPointerOver = (event: PointerEvent) => {
      const target = (event.target as HTMLElement).closest(
        "[data-tooltip]",
      ) as HTMLElement | null;
      if (!target || !container.contains(target)) {
        return;
      }
      if (activeTarget === target) {
        return;
      }
      activeTarget = target;
      positionLayer(target);
    };

    const onPointerOut = (event: PointerEvent) => {
      const from = (event.target as HTMLElement).closest(
        "[data-tooltip]",
      ) as HTMLElement | null;
      if (!from) {
        return;
      }
      const to = (event.relatedTarget as HTMLElement | null)?.closest(
        "[data-tooltip]",
      );
      if (from === to) {
        return;
      }
      hideLayer();
    };

    const onScroll = () => {
      if (activeTarget) {
        positionLayer(activeTarget);
      }
    };

    container.addEventListener("pointerover", onPointerOver);
    container.addEventListener("pointerout", onPointerOut);
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("pointerover", onPointerOver);
      container.removeEventListener("pointerout", onPointerOut);
      container.removeEventListener("scroll", onScroll);
      hideLayer();
    };
  }, [containerRef]);
}
